import { DashboardRangeEnum } from "@/lib/enum/dashboard-range.enum";
import { getUserTimezone } from "@/lib/utility/timezone-helper";

/**
 * Returns the wall-clock date/time parts of `date` as observed in `timeZone`, without
 * pulling in a timezone library — `Intl.DateTimeFormat` already carries the IANA database.
 */
function partsInTimeZone(date: Date, timezone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23"
  }).formatToParts(date);

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return {
    year: Number(get("year")),
    month: Number(get("month")),
    day: Number(get("day")),
    weekday: get("weekday"),
    hour: Number(get("hour")),
    minute: Number(get("minute")),
    second: Number(get("second"))
  };
}

/** Builds the UTC instant for local midnight on `year-month-day` in `timeZone`. */
function midnightInTimeZone(year: number, month: number, day: number, timezone: string): Date {
  // A UTC guess at local midnight is off by at most the zone's offset; comparing its own
  // rendered wall-clock time in `timeZone` against the target tells us that offset exactly,
  // without hardcoding DST rules or pulling in a timezone library.
  const utcGuess = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
  const rendered = partsInTimeZone(utcGuess, timezone);
  const renderedAsUtc = Date.UTC(
    rendered.year,
    rendered.month - 1,
    rendered.day,
    rendered.hour,
    rendered.minute,
    rendered.second
  );
  const offsetMs = renderedAsUtc - utcGuess.getTime();

  return new Date(utcGuess.getTime() - offsetMs);
}

/** Start of the local day (`timeZone` midnight) `date` falls in. */
function startOfDay(date: Date, timezone: string): Date {
  const { year, month, day } = partsInTimeZone(date, timezone);
  return midnightInTimeZone(year, month, day, timezone);
}

/** Start of the local ISO week (Monday) `date` falls in — matches Luxon's `startOf("week")`. */
function startOfWeek(date: Date, timezone: string): Date {
  const local = startOfDay(date, timezone);
  const { year, month, day } = partsInTimeZone(local, timezone);
  const utcMidnight = new Date(Date.UTC(year, month - 1, day));
  // getUTCDay() is Sun=0..Sat=6; remap to ISO Mon=0..Sun=6 since utcMidnight's
  // Y/M/D was built from the local calendar date, its UTC weekday matches the local one.
  const isoWeekday = (utcMidnight.getUTCDay() + 6) % 7;
  utcMidnight.setUTCDate(utcMidnight.getUTCDate() - isoWeekday);
  return midnightInTimeZone(
    utcMidnight.getUTCFullYear(),
    utcMidnight.getUTCMonth() + 1,
    utcMidnight.getUTCDate(),
    timezone
  );
}

/**
 * Convert a `DashboardRangeEnum` value to the ISO date range `{ from, to }` used by both
 * report-dashboard and inbound-invoice-dashboard's backend KPI queries, so a frontend
 * click-through that reconstructs the same range filters exactly what those endpoints
 * counted.
 *
 * Deliberately mirrors `resolveRangeWindow` (api/apps/api/src/lib/dashboard/bucket-timeline.helper.ts)
 * field for field:
 * - `to` is the exact current instant, not end-of-day — the backend's window is `[from, to)`
 *   anchored at "now", not a whole calendar day.
 * - `from` is 29 days / 12 weeks / 11 months / 4 years before "now" (not 30/12/12/5) so the
 *   window spans exactly `RANGE_30T`/`RANGE_90T`/`RANGE_12M`/`RANGE_5J`'s nominal length
 *   inclusive of today/this week/this month/this year.
 * - Both bounds are computed from the user's wall-clock day/week/month/year starts,
 *   not UTC — a UTC truncation can land on the wrong side of
 *   local midnight and shift the window by a day relative to what the backend counted.
 */
export function dashboardRangeToDateRange(range: DashboardRangeEnum): IHasRangeParams {
  const to = new Date();
  const timezone = getUserTimezone();
  let fromYear: number, fromMonth: number, fromDay: number;

  // Subtracting a duration from a calendar date, then re-anchoring that date to local
  // midnight, keeps the result correct across a DST transition — subtracting the
  // equivalent milliseconds directly from an instant would not, since local midnights
  // either side of a DST change aren't evenly spaced in UTC.
  switch (range) {
    case DashboardRangeEnum.RANGE_30T: {
      const { year, month, day } = partsInTimeZone(to, timezone);
      const target = new Date(Date.UTC(year, month - 1, day));
      target.setUTCDate(target.getUTCDate() - 29);
      fromYear = target.getUTCFullYear();
      fromMonth = target.getUTCMonth() + 1;
      fromDay = target.getUTCDate();
      break;
    }
    case DashboardRangeEnum.RANGE_90T: {
      const weekStart = startOfWeek(to, timezone);
      const { year, month, day } = partsInTimeZone(weekStart, timezone);
      const target = new Date(Date.UTC(year, month - 1, day));
      target.setUTCDate(target.getUTCDate() - 12 * 7);
      fromYear = target.getUTCFullYear();
      fromMonth = target.getUTCMonth() + 1;
      fromDay = target.getUTCDate();
      break;
    }
    case DashboardRangeEnum.RANGE_12M: {
      const { year, month } = partsInTimeZone(to, timezone);
      const target = new Date(Date.UTC(year, month - 1, 1));
      target.setUTCMonth(target.getUTCMonth() - 11);
      fromYear = target.getUTCFullYear();
      fromMonth = target.getUTCMonth() + 1;
      fromDay = 1;
      break;
    }
    case DashboardRangeEnum.RANGE_5J: {
      fromYear = partsInTimeZone(to, timezone).year - 4;
      fromMonth = 1;
      fromDay = 1;
      break;
    }
    default: {
      // Defensive fallback for a range value not (yet) covered above — e.g. the
      // backend adding a new DashboardRangeEnum member before the frontend enum
      // mirrors it. Degrade to RANGE_30T's window rather than throwing, since
      // callers source `range` from an unchecked cast of backend response data.
      const { year, month, day } = partsInTimeZone(to, timezone);
      const target = new Date(Date.UTC(year, month - 1, day));
      target.setUTCDate(target.getUTCDate() - 29);
      fromYear = target.getUTCFullYear();
      fromMonth = target.getUTCMonth() + 1;
      fromDay = target.getUTCDate();
    }
  }

  const from = midnightInTimeZone(fromYear, fromMonth, fromDay, timezone);

  return { from: from.toISOString(), to: to.toISOString() };
}

/**
 * Formats an ISO instant as its user-local calendar date (`YYYY-MM-DD`).
 *
 * Not `isoString.slice(0, 10)`: a local-midnight `from` boundary can fall on a different
 * UTC calendar date, so slicing the ISO string can shift a date-only backend filter by a day.
 */
export function toTimezoneDateString(isoInstant: string): string {
  const { year, month, day } = partsInTimeZone(new Date(isoInstant), getUserTimezone());
  return `${year.toString().padStart(4, "0")}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
}

/**
 * Converts a `string[]` date range (two ISO instants: [start, end]) into
 * timezone-aware `{ from, to }` ISO strings suitable for backend queries.
 *
 * - `from` = local midnight of the earlier date in the user's timezone
 * - `to` = end of local day (23:59:59.999) of the later date in the user's timezone
 *
 * Replaces the legacy `setUTCHours(0,0,0,0)` / `setUTCHours(20,59,59,999)` pattern
 * which truncated to the wrong hours and ignored the user's timezone.
 */
export function dateRangeStringsToRange(dateRange: string[]): { from: string; to: string } {
  const timezone = getUserTimezone();
  const timestamps = dateRange.map((d) => new Date(d).getTime());
  const minTs = Math.min.apply(null, timestamps);
  const maxTs = Math.max.apply(null, timestamps);

  const fromDate = new Date(minTs);
  const toDate = new Date(maxTs);

  const fromParts = partsInTimeZone(fromDate, timezone);
  const from = midnightInTimeZone(fromParts.year, fromParts.month, fromParts.day, timezone);

  const toParts = partsInTimeZone(toDate, timezone);
  const toMidnight = midnightInTimeZone(toParts.year, toParts.month, toParts.day, timezone);
  const to = new Date(toMidnight.getTime() + 24 * 60 * 60 * 1000 - 1);

  return { from: from.toISOString(), to: to.toISOString() };
}
